/**
 * Import profile: create, edit, preview, run.
 *
 * The form is generated from the per-app source schema, so the app's own keys
 * are the only ones ever submitted. Several rules are enforced in the UI
 * because the backend's answer to breaking them is a flat 400:
 *
 *   • **Permission is mandatory and deliberate.** `source` means the provider
 *     holds explicit evidence — not an assumption of consent. An attestation is
 *     the owner's own written statement, 10–500 characters, and it does not
 *     override a provider negative or an existing Merko suppression.
 *   • **Saving is a full replacement**, so an edit submits a clean
 *     `ProfileInput` built by `toProfileInput()`, never the `Profile` it came
 *     from.
 *   • **Discovery is truncated** at 100 options, so an explicit ID is always
 *     offered alongside whatever was discovered.
 *   • **`{id: "all"}` is a label, not a source.** Account-wide is an empty
 *     source, and the static discovery option is never submitted.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Database, Info, Loader2, Play, Trash2, X } from "lucide-react";
import { Button } from "components/ui/button";
import { SelectField } from "components/ui/select-field";
import { toast } from "components/ui/toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { useCustomerGroups } from "core/features/leads/hooks";
import {
    ATTESTATION_MAX,
    ATTESTATION_MIN,
    CONTACT_FIELDS,
    FREQUENCIES,
    MAX_PROFILES,
    PROFILE_STATUS_LABEL,
    SOURCE_SCHEMAS,
    STATIC_ALL_OPTION_ID,
    buildSource,
    describeFailure,
    emptyProfileInput,
    toProfileInput,
    useCreateProfile,
    useDeleteProfile,
    usePreviewProfile,
    useProfiles,
    useSources,
    useStartRun,
    useUpdateProfile,
    validateProfileInput,
} from "core/features/integrations";
import type {
    CatalogApp,
    ContactField,
    Frequency,
    Profile,
    ProfileInput,
} from "core/features/integrations";
import { ImportJobPanel } from "./ImportJobPanel";
import { ProfilePreview } from "./ProfilePreview";
import { ExternalWritePanel } from "./ExternalWritePanel";

const FIELD_LABEL: Record<ContactField, string> = {
    first_name: "First name",
    last_name: "Last name",
    email: "Email",
    phone_number: "Phone",
    whatsapp_number: "WhatsApp",
    messenger_id: "Messenger ID",
};

const FREQUENCY_LABEL: Record<Frequency, string> = {
    off: "Manual only",
    hourly: "Every hour",
    daily: "Once a day",
};

/** Statuses whose schedule stays stopped until the profile is re-saved. */
const NEEDS_RESAVE: string[] = ["paused_auth", "paused_quota", "failed"];

export interface ImportProfilePanelProps {
    app: CatalogApp;
    onClose: () => void;
}

export function ImportProfilePanel({ app, onClose }: ImportProfilePanelProps) {
    const schema = SOURCE_SCHEMAS[app.id];
    const profiles = useProfiles();
    const sources = useSources(app.id, { enabled: app.connectionStatus === "active" });
    const groups = useCustomerGroups({ pageSize: 100 });

    const create = useCreateProfile();
    const update = useUpdateProfile();
    const remove = useDeleteProfile();
    const preview = usePreviewProfile();
    const run = useStartRun();

    const existing = useMemo(
        () => (profiles.data ?? []).filter((p) => p.app_id === app.id),
        [profiles.data, app.id],
    );

    const [editing, setEditing] = useState<Profile | null>(null);
    const [draft, setDraft] = useState<ProfileInput>(() => emptyProfileInput(app.id));
    const [sourceValues, setSourceValues] = useState<Record<string, string>>({});
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<Profile | null>(null);

    /** Load a stored profile into the form as a clean, submittable input. */
    const startEdit = useCallback((profile: Profile) => {
        setEditing(profile);
        setActiveJobId(null);
        setDraft(toProfileInput(profile));
        setSourceValues(
            Object.fromEntries(
                Object.entries(profile.source).map(([key, value]) => [key, String(value)]),
            ),
        );
    }, []);

    const startNew = useCallback(() => {
        setEditing(null);
        setActiveJobId(null);
        setDraft(emptyProfileInput(app.id));
        setSourceValues({});
    }, [app.id]);

    useEffect(() => {
        startNew();
    }, [startNew]);

    const source = useMemo(() => buildSource(app.id, sourceValues), [app.id, sourceValues]);
    const candidate: ProfileInput = useMemo(() => ({ ...draft, source }), [draft, source]);
    const validation = useMemo(() => validateProfileInput(candidate), [candidate]);

    /* Discovery is capped at 100 and normally truncated, so the listed options
       are a convenience and the explicit input is the guarantee. */
    const discovered = useMemo(() => {
        const list = sources.data?.sources ?? [];
        // The static option an app without discovery returns is a label only.
        return list.filter((option) => option.id !== STATIC_ALL_OPTION_ID);
    }, [sources.data]);
    const mayHaveMore = !!sources.data?.may_have_more;
    /* Apps with no discovery tool answer with the static option plus the fields
       the user has to supply themselves. */
    const requiredFields: string[] =
        sources.data && "required_fields" in sources.data
            ? sources.data.required_fields ?? []
            : [];

    const groupName = (groupId: number): string =>
        (groups.data?.data ?? []).find((group) => group.id === groupId)?.name ??
        `Audience ${groupId}`;

    const atLimit = (profiles.data?.length ?? 0) >= MAX_PROFILES;

    const save = useCallback(async () => {
        if (!validation.valid) return;
        try {
            if (editing) {
                // Full replacement — `candidate` is already a clean ProfileInput.
                const saved = await update.mutateAsync({ profileId: editing.id, input: candidate });
                setEditing(saved);
                toast({ title: "Import profile saved" });
            } else {
                const saved = await create.mutateAsync(candidate);
                setEditing(saved);
                toast({ title: "Import profile created" });
            }
        } catch (error) {
            const failure = describeFailure(error);
            toast({
                title:
                    failure.code === "job_running"
                        ? "An import is running"
                        : failure.code === "profile_limit"
                          ? "Profile limit reached"
                          : "Couldn’t save the profile",
                description: failure.message,
                variant: "error",
            });
        }
    }, [candidate, create, editing, update, validation.valid]);

    const syncNow = useCallback(async () => {
        if (!editing) return;
        try {
            const job = await run.mutateAsync(editing.id);
            setActiveJobId(job.id);
            // 202 is queued, not imported.
            toast({ title: "Import queued", description: "It will run in the background." });
        } catch (error) {
            const failure = describeFailure(error);
            toast({
                title: failure.code === "job_running" ? "Already running" : "Couldn’t start the import",
                description: failure.message,
                variant: "error",
            });
        }
    }, [editing, run]);

    const confirmDelete = useCallback(async () => {
        const profile = pendingDelete;
        if (!profile) return;
        setPendingDelete(null);
        try {
            await remove.mutateAsync(profile.id);
            if (editing?.id === profile.id) startNew();
            toast({
                title: "Profile deleted",
                description: "The contacts it imported have been kept.",
            });
        } catch (error) {
            const failure = describeFailure(error);
            toast({
                title: failure.code === "job_running" ? "An import is running" : "Couldn’t delete",
                description: failure.message,
                variant: "error",
            });
        }
    }, [editing, pendingDelete, remove, startNew]);

    // WordPress.com has no contact import at all.
    if (!schema?.contactImport) {
        return (
            <Dialog open onOpenChange={(open) => !open && onClose()}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>{app.name}</DialogTitle>
                        <DialogDescription>
                            This app does not import contacts. It is used for content only — its
                            sources are site IDs for creating drafts.
                        </DialogDescription>
                    </DialogHeader>
                    {/* Content controls only, and only if writes are certified
                        and enabled for this app — the panel hides itself
                        otherwise. */}
                    <div className="pt-4">
                        <ExternalWritePanel appId={app.id} appName={app.name} />
                    </div>
                    <DialogFooter>
                        <Button onClick={onClose}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    const busy = create.isPending || update.isPending;
    const jobRunning = !!editing?.active_job;

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Import contacts from {app.name}</DialogTitle>
                    <DialogDescription>{schema.note}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-4">
                    {existing.length > 0 && (
                        <section>
                            <h3 className="mb-2 text-ui font-semibold text-content">
                                Existing profiles
                            </h3>
                            <ul className="space-y-2">
                                {existing.map((profile) => (
                                    <li
                                        key={profile.id}
                                        className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                                    >
                                        <div className="min-w-0">
                                            <p className="truncate text-ui text-content">
                                                {groupName(profile.group_id)} ·{" "}
                                                {FREQUENCY_LABEL[profile.frequency]}
                                            </p>
                                            <p className="text-meta text-content-muted">
                                                {PROFILE_STATUS_LABEL[profile.status] ?? profile.status}
                                                {profile.error_code ? ` · ${profile.error_code}` : ""}
                                                {profile.active_job ? " · import running" : ""}
                                            </p>
                                            {NEEDS_RESAVE.includes(profile.status) && (
                                                <p className="text-meta text-amber-700 dark:text-amber-300">
                                                    This will not restart on its own. Fix the cause,
                                                    then open it and save it again.
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => startEdit(profile)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                aria-label="Delete profile"
                                                onClick={() => setPendingDelete(profile)}
                                            >
                                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {editing && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="mt-2"
                                    onClick={startNew}
                                >
                                    Start a new profile instead
                                </Button>
                            )}
                        </section>
                    )}

                    {atLimit && !editing && (
                        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-meta text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            You have reached the maximum of {MAX_PROFILES} import profiles. Delete one
                            before creating another.
                        </p>
                    )}

                    {/* ── audience ── */}
                    <section>
                        <label
                            htmlFor="profile-group"
                            className="block text-ui font-semibold text-content"
                        >
                            Import into
                        </label>
                        <SelectField
                            id="profile-group"
                            value={draft.group_id || ""}
                            onChange={(event) =>
                                setDraft((d) => ({ ...d, group_id: Number(event.target.value) }))
                            }
                            className="mt-1.5 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring"
                        >
                            <option value="">Choose an audience…</option>
                            {(groups.data?.data ?? []).map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </SelectField>
                        {validation.errors.group && (
                            <p role="alert" className="mt-1 text-meta text-red-600 dark:text-red-400">
                                {validation.errors.group}
                            </p>
                        )}
                    </section>

                    {/* ── source ── */}
                    <section>
                        <h3 className="text-ui font-semibold text-content">Source</h3>
                        {schema.accountWide && (
                            <p className="mt-1 text-meta text-content-muted">
                                Leave these blank to import account-wide.
                            </p>
                        )}

                        {schema.fields.length === 0 ? (
                            <p className="mt-1.5 text-meta text-content-muted">
                                Nothing to choose — this app imports account-wide contacts.
                            </p>
                        ) : (
                            <div className="mt-2 space-y-3">
                                {schema.fields.map((field) => {
                                    const options = field.choices
                                        ? field.choices.map((choice) => ({ id: choice, name: choice }))
                                        : field.discoverable
                                          ? discovered
                                          : [];
                                    const picked = sourceValues[field.key] ?? "";
                                    /* A value loaded from a stored profile that is
                                       valid but not among the listed choices (the
                                       singular "Contact"/"Lead") is kept selectable
                                       rather than silently reset. */
                                    const extra =
                                        field.choices && picked && !options.some((o) => o.id === picked)
                                            ? [{ id: picked, name: picked }]
                                            : [];
                                    return (
                                        <div key={field.key}>
                                            <label
                                                htmlFor={`source-${field.key}`}
                                                className="block text-meta font-medium text-content"
                                            >
                                                {field.label}
                                                {field.required && " *"}
                                            </label>

                                            {options.length > 0 && (
                                                <SelectField
                                                    aria-label={`${field.label} — discovered options`}
                                                    value={
                                                        options.some(
                                                            (o) => o.id === sourceValues[field.key],
                                                        )
                                                            ? sourceValues[field.key]
                                                            : ""
                                                    }
                                                    onChange={(event) =>
                                                        setSourceValues((v) => ({
                                                            ...v,
                                                            [field.key]: event.target.value,
                                                        }))
                                                    }
                                                    className="mt-1.5 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring"
                                                >
                                                    <option value="">
                                                        {field.choices
                                                            ? `Default (${field.choices[0]})`
                                                            : schema.accountWide
                                                              ? "Account-wide"
                                                              : "Choose…"}
                                                    </option>
                                                    {[...options, ...extra].map((option) => (
                                                        <option key={option.id} value={option.id}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                                </SelectField>
                                            )}

                                            {/* Always available for an ID: discovery is
                                                truncated, so an unlisted one must still be
                                                enterable. A closed choice has no such case. */}
                                            {!field.choices && (
                                            <input
                                                id={`source-${field.key}`}
                                                value={sourceValues[field.key] ?? ""}
                                                onChange={(event) =>
                                                    setSourceValues((v) => ({
                                                        ...v,
                                                        [field.key]: event.target.value,
                                                    }))
                                                }
                                                inputMode={
                                                    field.kind === "integer" ||
                                                    field.kind === "decimal-string"
                                                        ? "numeric"
                                                        : "text"
                                                }
                                                placeholder={
                                                    options.length > 0
                                                        ? "…or type an ID that isn’t listed"
                                                        : field.hint ?? ""
                                                }
                                                className="mt-1.5 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring"
                                            />
                                            )}
                                            {field.hint && (options.length > 0 || !!field.choices) && (
                                                <p className="mt-1 text-meta text-content-muted">
                                                    {field.hint}
                                                </p>
                                            )}
                                            {validation.sourceErrors[field.key] && (
                                                <p role="alert" className="mt-1 text-meta text-red-600 dark:text-red-400">
                                                    {validation.sourceErrors[field.key]}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}

                                {requiredFields.length > 0 && (
                                    <p className="flex items-start gap-1.5 text-meta text-content-muted">
                                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        This source needs: {requiredFields.join(", ")}.
                                    </p>
                                )}

                                {mayHaveMore && (
                                    <p className="flex items-start gap-1.5 text-meta text-content-muted">
                                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        Only the first 100 options are listed. Type an ID directly if
                                        yours is not shown.
                                    </p>
                                )}
                            </div>
                        )}
                    </section>

                    {/* ── mapping ── */}
                    <section>
                        <h3 className="text-ui font-semibold text-content">Field mapping</h3>
                        <p className="mt-1 text-meta text-content-muted">
                            Optional. Give the provider’s field name, header or answer ID for each
                            one you want mapped.
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            {CONTACT_FIELDS.map((field) => (
                                <div key={field}>
                                    <label
                                        htmlFor={`map-${field}`}
                                        className="block text-meta font-medium text-content"
                                    >
                                        {FIELD_LABEL[field]}
                                    </label>
                                    <input
                                        id={`map-${field}`}
                                        value={draft.mapping[field] ?? ""}
                                        onChange={(event) =>
                                            setDraft((d) => {
                                                const mapping = { ...d.mapping };
                                                const value = event.target.value;
                                                if (value) mapping[field] = value;
                                                else delete mapping[field];
                                                return { ...d, mapping };
                                            })
                                        }
                                        className="mt-1 h-9 w-full rounded-lg border border-line bg-surface px-2.5 text-meta text-content focus-ring"
                                    />
                                    {validation.mappingErrors[field] && (
                                        <p role="alert" className="mt-1 text-meta text-red-600 dark:text-red-400">
                                            {validation.mappingErrors[field]}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── permission ── */}
                    <section>
                        <h3 className="text-ui font-semibold text-content">
                            How do you have permission? *
                        </h3>
                        <div className="mt-2 space-y-2">
                            <PermissionChoice
                                checked={draft.permission.mode === "source"}
                                onSelect={() => setDraft((d) => ({ ...d, permission: { mode: "source" } }))}
                                title="The provider holds the evidence"
                                detail="Use the provider’s own explicit permission record. This does not assume everyone is subscribed."
                            />
                            <PermissionChoice
                                checked={draft.permission.mode === "mapped"}
                                onSelect={() =>
                                    setDraft((d) => ({ ...d, permission: { mode: "mapped", field: "" } }))
                                }
                                title="A field holds the evidence"
                                detail="Name the provider field that records the opt-in."
                            >
                                {draft.permission.mode === "mapped" && (
                                    <input
                                        aria-label="Provider field holding opt-in evidence"
                                        value={draft.permission.field}
                                        onChange={(event) =>
                                            setDraft((d) => ({
                                                ...d,
                                                permission: { mode: "mapped", field: event.target.value },
                                            }))
                                        }
                                        placeholder="e.g. consent_given"
                                        className="mt-2 h-9 w-full rounded-lg border border-line bg-surface px-2.5 text-meta text-content focus-ring"
                                    />
                                )}
                            </PermissionChoice>
                            <PermissionChoice
                                checked={draft.permission.mode === "attested"}
                                onSelect={() =>
                                    setDraft((d) => ({
                                        ...d,
                                        permission: { mode: "attested", attestation: "" },
                                    }))
                                }
                                title="You attest to it yourself"
                                detail={`Write how you have consent, in ${ATTESTATION_MIN}–${ATTESTATION_MAX} characters. This does not override a provider opt-out or an existing suppression.`}
                            >
                                {draft.permission.mode === "attested" && (
                                    <textarea
                                        aria-label="Your attestation"
                                        value={draft.permission.attestation}
                                        onChange={(event) =>
                                            setDraft((d) => ({
                                                ...d,
                                                permission: {
                                                    mode: "attested",
                                                    attestation: event.target.value,
                                                },
                                            }))
                                        }
                                        rows={3}
                                        maxLength={ATTESTATION_MAX}
                                        placeholder="These contacts opted in at…"
                                        className="mt-2 w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-meta text-content focus-ring"
                                    />
                                )}
                            </PermissionChoice>
                        </div>
                        {validation.errors.permission && (
                            <p role="alert" className="mt-1 text-meta text-red-600 dark:text-red-400">
                                {validation.errors.permission}
                            </p>
                        )}
                    </section>

                    {/* ── frequency ── */}
                    <section>
                        <label
                            htmlFor="profile-frequency"
                            className="block text-ui font-semibold text-content"
                        >
                            Sync schedule
                        </label>
                        <SelectField
                            id="profile-frequency"
                            value={draft.frequency}
                            onChange={(event) =>
                                setDraft((d) => ({ ...d, frequency: event.target.value as Frequency }))
                            }
                            className="mt-1.5 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring"
                        >
                            {FREQUENCIES.map((frequency) => (
                                <option key={frequency} value={frequency}>
                                    {FREQUENCY_LABEL[frequency]}
                                </option>
                            ))}
                        </SelectField>
                        <p className="mt-1 text-meta text-content-muted">
                            {draft.frequency === "off"
                                ? "Nothing runs automatically. You can still import manually at any time."
                                : "Scheduled syncs are one-way and may start a few minutes after they are due."}
                        </p>
                    </section>

                    {editing && (
                        <>
                            <ProfilePreview
                                profile={editing}
                                preview={preview.data ?? null}
                                loading={preview.isPending}
                                failure={preview.error ? describeFailure(preview.error) : null}
                                onPreview={() => void preview.mutateAsync(editing.id).catch(() => {})}
                            />
                            {(activeJobId ?? editing.active_job) && (
                                <ImportJobPanel
                                    jobId={(activeJobId ?? editing.active_job) as string}
                                    profileId={editing.id}
                                    onRetried={(job) => setActiveJobId(job.id)}
                                />
                            )}
                        </>
                    )}

                    <ExternalWritePanel appId={app.id} appName={app.name} />
                </div>

                <DialogFooter className="mt-2">
                    <Button variant="ghost" onClick={onClose}>
                        <X className="h-4 w-4" aria-hidden="true" />
                        Close
                    </Button>

                    {editing && (
                        <Button
                            variant="secondary"
                            loading={run.isPending}
                            disabled={jobRunning}
                            onClick={() => void syncNow()}
                            title={jobRunning ? "An import is already running" : undefined}
                        >
                            <Play className="h-4 w-4" aria-hidden="true" />
                            Sync now
                        </Button>
                    )}

                    <Button
                        variant="primary"
                        loading={busy}
                        disabled={!validation.valid || (atLimit && !editing) || jobRunning}
                        onClick={() => void save()}
                    >
                        <Database className="h-4 w-4" aria-hidden="true" />
                        {editing ? "Save profile" : "Create profile"}
                    </Button>
                </DialogFooter>

                {jobRunning && (
                    <p className="flex items-center gap-1.5 text-meta text-content-muted">
                        <Loader2
                            className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                            aria-hidden="true"
                        />
                        An import is running for this profile — it cannot be edited or deleted until
                        that finishes.
                    </p>
                )}
            </DialogContent>

            <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>Delete this import profile?</DialogTitle>
                        <DialogDescription>
                            The contacts it has already imported are kept. Only the profile and its
                            schedule are removed.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPendingDelete(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            loading={remove.isPending}
                            onClick={() => void confirmDelete()}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

function PermissionChoice({
    checked,
    onSelect,
    title,
    detail,
    children,
}: {
    checked: boolean;
    onSelect: () => void;
    title: string;
    detail: string;
    children?: React.ReactNode;
}) {
    return (
        <div
            className={
                checked
                    ? "rounded-lg border border-accent bg-accent/5 p-3"
                    : "rounded-lg border border-line p-3"
            }
        >
            <label className="flex cursor-pointer items-start gap-2">
                <input
                    type="radio"
                    name="permission-mode"
                    checked={checked}
                    onChange={onSelect}
                    className="mt-1"
                />
                <span>
                    <span className="block text-ui font-medium text-content">{title}</span>
                    <span className="block text-meta text-content-muted">{detail}</span>
                </span>
            </label>
            {children}
        </div>
    );
}
